/**
 * ResultsGridLayout utility functions
 * @module ResultsGridLayout/utils
 */

'use strict';

import {
    updateFiltersStateHistoryPage,
    updateLastSelectedElement,
} from '@core/utils/history-state';
import { goToDetailsViewOrReportDetails } from '@core/utils/navigate';
import { getTypeFromSection } from '@core/utils/report';
import { ZOOM_DOWN, ZOOM_UP } from '@core/constants/CONSTANTS';
import { SEARCHER_TYPES } from '@core/constants/SEARCHER';
import { DATA_ID_ATTRIBUTE } from '@core/constants/ATTRIBUTES';
import { customDebounce, getMainBodyBaseLayout } from '@core/utils/utils';
import { getStoreConfiguration } from '@core/utils/store';
import { WESOG_STORE_CONFIGURATION_PARAMS } from '@core/constants/STORE';
import { ROUTES } from '@core/constants/ROUTES';
import { TRIGGER_SEARCH_QUERY_EVENT } from '@core/constants/EVENTS';
import { SEARCH_IMG_QUERY_PARAMS } from '@core/constants/API';
import { generateCode } from '@core/utils/uuid';
import { openLink, cleanTextToCards } from '@core/utils/utils';
import { getSearchOptionUrl, getAiFromUrl } from '@core/utils/searcher';
import { addStoreFiltersState } from '@core/utils/store';
import { TABS_LAYOUT_WEB_COMPONENT } from '@components/layouts/TabsLayout/CONSTANTS';
import { CUSTOM_MAIN_LOADING_SPINNER_WEB_COMPONENT } from '@core/constants/WEB-COMPONENT-KEYS';
import { MIN_ACTIVES_HEIGHT } from '@core/constants/DEFAULT';

import {
    SEARCH_IMG_CARD_WEB_COMPONENT,
    SEARCH_IMG_CARD_ATTRIBUTES,
} from './components/SearchImgCardComponent/CONSTANTS';
import {
    RESULTS_GRID_LAYOUT_WEB_COMPONENT,
    CUSTOM_RESULTS_GRID_LOADING_SPINNER_WEB_COMPONENT,
} from './CONSTANTS';
import {
    REPORT_CARD_ATTRIBUTES,
    REPORT_CARD_COMPONENT,
} from './components/ReportCardComponent/CONSTANTS';
import { DEFAULT_ITEM } from './CONSTANTS';
import { SERVER_MAINTENANCE_WEB_COMPONENT } from '@components/ServerMaintenanceComponent/CONSTANTS';

/**
 * Show a notification when the AI server is offline.
 * @param {ShadowRoot} shadowRoot - The shadow root.
 * @param {string} querySelectorName - The query selector name.
 * @returns {void}
 */
export const showAIServerOffline = (shadowRoot, querySelectorName) => {
    /** @type {HTMLElement} */
    const container = shadowRoot.querySelector(querySelectorName);
    /** @type {HTMLElement} */
    const serverMaintenanceResults = document.createElement(
        SERVER_MAINTENANCE_WEB_COMPONENT,
    );

    container.innerHTML = '';
    container.appendChild(serverMaintenanceResults);
};

/**
 * From the zoom configuration percent return the correct zoom value.
 * @returns {Number} The correct zoom number.
 */
export const calculateZoomValue = () => {
    const /** @type {Object} */ configuration = getStoreConfiguration();
    const /** @type {Number} */ currentZoom =
            configuration[WESOG_STORE_CONFIGURATION_PARAMS.ZOOM];

    if (!currentZoom) return ZOOM_UP;

    return (ZOOM_UP * currentZoom) / 100;
};

/**
 * Create the report element and add it to the container grid layout
 * @param {object} report - Object with the report data.
 * @param {HTMLElement} container - Container to add the report.
 * @param {string} attributeTitle - Attribute title for the report.
 * @param {string} [cardElement] - Element to add the report. Default: REPORT_CARD_COMPONENT.
 * @param {number} [minImgHeight] - Min height for the report. Default: undefined.
 * @param {string} [currentReportType] - Current report type. Default: ''.
 * @param {HTMLElement} [firstContainerElement] - First container element. Default: null.
 * @returns {void}
 */
export const reportCardFn = (
    report,
    container,
    attributeTitle,
    cardElement = REPORT_CARD_COMPONENT,
    minImgHeight = MIN_ACTIVES_HEIGHT,
    currentReportType = '',
    firstContainerElement = null,
) => {
    /** @type {HTMLElement} */
    const imageElement = document.createElement(cardElement);

    minImgHeight = minImgHeight * calculateZoomValue();

    if (!report.defaultItem) report.defaultItem = DEFAULT_ITEM;

    report.defaultItem.img_width = report?.defaultItem?.img_width || 'auto';
    report.defaultItem.img_height = report?.defaultItem?.img_height || minImgHeight;

    imageElement.setAttribute(
        REPORT_CARD_ATTRIBUTES.IMAGE,
        JSON.stringify(report.defaultItem),
    );
    imageElement.setAttribute(REPORT_CARD_ATTRIBUTES.MIN_IMG_HEIGHT, minImgHeight);
    imageElement.setAttribute(
        REPORT_CARD_ATTRIBUTES.REPORT_TITLE,
        cleanTextToCards(report.title || report?.titleChart || attributeTitle || ''),
    );
    imageElement.setAttribute(
        REPORT_CARD_ATTRIBUTES.DATE,
        report?.defaultItem?.date || report.date || report?.dateChart || '',
    );
    imageElement.setAttribute(
        REPORT_CARD_ATTRIBUTES.PHOTOS,
        Array.isArray(report.elements)
            ? report.elements.length
            : report.elements || report.counter,
    );
    const /** @type {string|number} */ elementId = report.uid || report?.id;
    imageElement.setAttribute(REPORT_CARD_ATTRIBUTES.ID, elementId);
    imageElement.setAttribute(
        REPORT_CARD_ATTRIBUTES.TYPE_REPORT,
        getTypeFromSection(report.section || ''),
    );

    if (currentReportType)
        imageElement.setAttribute(
            REPORT_CARD_ATTRIBUTES.CURRENT_REPORT_TYPE,
            currentReportType,
        );
    else {
        imageElement.setAttribute(REPORT_CARD_ATTRIBUTES.LIGHTBOX, true);
    }

    imageElement.setAttribute(REPORT_CARD_ATTRIBUTES.PDF, report.pdfFileName);

    /* We add this 'click' event here because we need the whole report object to use the goToDetailsViewOrReportDetails function */
    imageElement.addEventListener('click', (e) => {
        e.stopPropagation();
        const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
                window.location.search,
            );
        let /** @type {string} */ newUrl = '';

        if (!window.location.pathname.includes(ROUTES.DETAILS)) {
            updateFiltersStateHistoryPage(report.defaultItem.current_page || 0);
            updateLastSelectedElement(elementId);
        }

        setTimeout(() => {
            if (!currentReportType) {
                const /** @type {string} */ searcherIdCode = generateCode();

                newUrl = `${ROUTES.REPORT_DETAILS}?${SEARCHER_TYPES.LIGHTBOX_ID}=${elementId}&${SEARCHER_TYPES.SEARCHER_ID}=${searcherIdCode}`;

                addStoreFiltersState(searcherIdCode);
                newUrl = `${newUrl}&${SEARCHER_TYPES.OPTION}=${getSearchOptionUrl(
                    urlParams,
                )}&${SEARCHER_TYPES.AI}=${getAiFromUrl(urlParams)}`;

                openLink(newUrl);
            } else {
                goToDetailsViewOrReportDetails(report);
            }
        }, 500);
    });

    if (firstContainerElement) {
        container.insertBefore(imageElement, firstContainerElement);
    } else {
        container.appendChild(imageElement);
    }
};

/**
 * Function to create the img element and add it to the container grid layout
 * @param {object} img - Object with the image data
 * @param {HTMLElement} container - Container to add the img
 * @param {number} minImgHeight - Min height for the img
 * @param {boolean} [zoomIn] - Flag to zoom in the img. Default: false
 * @param {HTMLElement} [firstContainerElement] - First container element. Default: null
 * @returns {void}
 */
export const searchImgElement = (
    img,
    container,
    minImgHeight,
    zoomIn = false,
    firstContainerElement = null,
) => {
    /* Up all the size values a 20% */
    if (zoomIn) {
        minImgHeight = minImgHeight * calculateZoomValue();
        img.img_width = img.img_width || 'auto';
        img.img_height = img.img_height || minImgHeight;
    } else {
        minImgHeight = minImgHeight * ZOOM_DOWN;
        img.img_width = img.img_width ? img.img_width : 'auto';
        img.img_height = img.img_height ? img.img_height : minImgHeight;
    }

    const /** @type {HTMLElement} */ searchCardElement = document.createElement(
            SEARCH_IMG_CARD_WEB_COMPONENT,
        );

    searchCardElement.setAttribute(
        SEARCH_IMG_CARD_ATTRIBUTES.ACTIVE_ID,
        img.uid || img.id,
    );
    searchCardElement.setAttribute(SEARCH_IMG_CARD_ATTRIBUTES.IMAGE, JSON.stringify(img));
    searchCardElement.setAttribute(
        SEARCH_IMG_CARD_ATTRIBUTES.MIN_IMG_HEIGHT,
        minImgHeight,
    );

    if (!img.img_width || img.img_width === 'auto' || img.img_width === 'NaN') {
        container.classList.add('mock-report-details-card');
    }

    /** @type {URLSearchParams} */
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has(SEARCHER_TYPES.LIGHTBOX_ID)) {
        searchCardElement.setAttribute(
            SEARCH_IMG_CARD_ATTRIBUTES.LIGHTBOX_ITEM,
            urlParams.get(SEARCHER_TYPES.LIGHTBOX_ID),
        );
    }

    if (firstContainerElement) {
        container.insertBefore(searchCardElement, firstContainerElement);
    } else {
        container.appendChild(searchCardElement);
    }

    return;
};

/**
 * Get the grid layout web component.
 * @returns {HTMLElement}
 */
export const getGridLayoutWebComponent = () => {
    const /** @type {HTMLElement | null} */ tabsLayout =
            getMainBodyBaseLayout()?.querySelector(TABS_LAYOUT_WEB_COMPONENT);

    if (!tabsLayout) {
        return document.querySelector(RESULTS_GRID_LAYOUT_WEB_COMPONENT);
    }

    return tabsLayout.shadowRoot?.querySelector(RESULTS_GRID_LAYOUT_WEB_COMPONENT);
};

/**
 * Get the grid spinner.
 * @returns {HTMLElement}
 */
export const getGridSpinner = () => {
    const /** @type {HTMLElement} */ gridLayout = getGridLayoutWebComponent();

    return gridLayout.querySelector(CUSTOM_RESULTS_GRID_LOADING_SPINNER_WEB_COMPONENT);
};

/**
 * Check if the AI is activated and the current view is not the details view.
 * @param {boolean} isAIActivated - Whether AI is activated.
 * @param {string} currentView - The current view.
 * @returns {boolean} - True if AI is activated and not in details view.
 */
export const isAIActivatedAndNotDetailsView = (isAIActivated, currentView) => {
    return isAIActivated && currentView !== ROUTES.DETAILS;
};

/**
 * Create a debounced scroll handler for intersection observers.
 * @param {Function} callback - The callback function to execute.
 * @returns {Function} - The debounced function.
 */
export const createDebouncedScrollHandler = (callback) => {
    return customDebounce(callback, 100);
};

/**
 * Check if entries are intersecting.
 * @param {IntersectionObserverEntry[]} entries - The intersection observer entries.
 * @returns {boolean} - True if any entry is intersecting.
 */
export const checkEntries = (entries) => {
    return entries.some((entry) => entry.isIntersecting);
};

/**
 * Create an intersection observer for infinite scroll.
 * @param {Function} loadMoreEntries - The function to call when intersection occurs.
 * @param {string} rootMargin - The root margin for the observer.
 * @returns {IntersectionObserver} - The intersection observer.
 */
export const createIntersectionObserver = (loadMoreEntries, rootMargin = '1200px') => {
    return new IntersectionObserver(loadMoreEntries, {
        root: null,
        rootMargin,
        threshold: 0,
    });
};

/**
 * Render grid elements based on the current view.
 * @param {Array} data - The data to render.
 * @param {HTMLElement} container - The container element.
 * @param {string} currentView - The current view.
 * @param {string} currentReportType - The current report type.
 * @param {string} title - The title attribute.
 * @param {boolean} searchImgsResults - Whether showing search images results.
 * @param {number} minImgHeight - The minimum image height.
 * @param {HTMLElement} [insertBeforeElement] - Element to insert before.
 * @returns {void}
 */
export const renderGridElements = (
    data,
    container,
    currentView,
    currentReportType,
    title,
    searchImgsResults,
    minImgHeight = MIN_ACTIVES_HEIGHT,
    insertBeforeElement = null,
) => {
    data.forEach((element) => {
        switch (currentView) {
            case ROUTES.RESULTS:
            case ROUTES.REPORT_DETAILS:
                searchImgElement(
                    element,
                    container,
                    minImgHeight,
                    true,
                    insertBeforeElement,
                );
                break;
            case ROUTES.DETAILS:
                searchImgElement(
                    element,
                    container,
                    minImgHeight,
                    false,
                    insertBeforeElement,
                );
                break;
            case ROUTES.REPORTS_LIST:
            case ROUTES.LIGHT_BOX:
                reportCardFn(
                    element,
                    container,
                    title,
                    REPORT_CARD_COMPONENT,
                    minImgHeight,
                    currentReportType,
                    insertBeforeElement,
                );
                break;
            default:
                break;
        }
    });
};

/**
 * Get the selected element by ID.
 * @param {HTMLElement} container - The container element.
 * @param {string} lastSelectedElementId - The last selected element ID.
 * @param {string} currentView - The current view.
 * @param {boolean} searchImgsResults - Whether showing search images results.
 * @returns {HTMLElement|null} - The selected element or null.
 */
export const getSelectedElement = (
    container,
    lastSelectedElementId,
    currentView,
    searchImgsResults,
) => {
    if (!lastSelectedElementId) return null;

    const selector =
        [ROUTES.RESULTS, ROUTES.REPORT_DETAILS].includes(currentView) && searchImgsResults
            ? SEARCH_IMG_CARD_WEB_COMPONENT
            : REPORT_CARD_COMPONENT;

    const elements = container.querySelectorAll(selector);

    for (const element of elements) {
        if (
            parseInt(element.getAttribute(DATA_ID_ATTRIBUTE)) ===
            parseInt(lastSelectedElementId)
        ) {
            return element;
        }
    }

    return null;
};

/**
 * Show loading spinner.
 * @param {Object} params - The parameters.
 * @param {HTMLElement} params.shadowRoot - The shadow root element.
 * @param {boolean} [params.unauthorized=false] - Whether unauthorized.
 * @param {boolean} [params.isDetailsView=true] - Whether is details view.
 * @returns {void}
 */
export const showLoadingSpinner = ({
    shadowRoot,
    unauthorized = false,
    isDetailsView = true,
}) => {
    const loading = shadowRoot.querySelector(
        CUSTOM_RESULTS_GRID_LOADING_SPINNER_WEB_COMPONENT,
    );
    const mainLoading = document.querySelector(CUSTOM_MAIN_LOADING_SPINNER_WEB_COMPONENT);

    if (mainLoading && !isDetailsView) {
        mainLoading.style.display = unauthorized ? 'flex' : 'none';
    }

    if (loading && isDetailsView) {
        loading.style.display = unauthorized ? 'flex' : 'none';
    }
};

/**
 * Hide loading spinner.
 * @param {HTMLElement} shadowRoot - The shadow root element.
 * @returns {void}
 */
export const hideLoadingSpinner = (shadowRoot) => {
    const loading = shadowRoot.querySelector(
        CUSTOM_RESULTS_GRID_LOADING_SPINNER_WEB_COMPONENT,
    );
    const mainLoading = document.querySelector(CUSTOM_MAIN_LOADING_SPINNER_WEB_COMPONENT);

    if (mainLoading) {
        mainLoading.style.display = 'none';
    }

    if (loading) {
        loading.style.display = 'none';
    }
};

/**
 * Get pagination configuration.
 * @returns {boolean} - The pagination configuration value.
 */
export const getPaginationConfig = () => {
    const configuration = getStoreConfiguration();
    return configuration[WESOG_STORE_CONFIGURATION_PARAMS.PAGINATION];
};

/**
 * Get per page configuration.
 * @returns {number} - The per page configuration value.
 */
export const getPerPageConfig = () => {
    const configuration = getStoreConfiguration();
    return configuration[WESOG_STORE_CONFIGURATION_PARAMS.PER_PAGE];
};

/**
 * Calculate total pagination.
 * @param {number} totalElements - The total number of elements.
 * @returns {number} - The total pagination.
 */
export const calculateTotalPagination = (totalElements) => {
    const perPageConfig = getPerPageConfig();
    return Math.ceil(totalElements / perPageConfig);
};

/**
 * Dispatch search query event.
 * @param {Object} detail - The event detail.
 * @returns {void}
 */
export const dispatchSearchQueryEvent = (detail, cancelable = false) => {
    document.dispatchEvent(
        new CustomEvent(TRIGGER_SEARCH_QUERY_EVENT, { detail, cancelable }),
    );
};

/**
 * Scroll to top smoothly.
 * @returns {void}
 */
export const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * Scroll to element smoothly.
 * @param {HTMLElement} element - The element to scroll to.
 * @returns {void}
 */
export const scrollToElement = (element) => {
    if (element) {
        window.scrollTo({
            top: element.offsetTop,
            behavior: 'smooth',
        });
    }
};

/**
 * Fetch more content from the API.
 * @param {Object} extraOptions - Extra options to fetch more content.
 * @returns {void}
 * @private
 */
export const fetchMoreContent = (
    currentPage,
    searchReportsResults,
    extraOptions = {},
) => {
    const newValue = currentPage + 1;

    dispatchSearchQueryEvent({
        ...(!searchReportsResults && {
            [SEARCH_IMG_QUERY_PARAMS.PAGE_AI]: newValue,
        }),
        ...(searchReportsResults && {
            [SEARCH_IMG_QUERY_PARAMS.PAGE_REPORT_AI]: newValue,
        }),
        ...extraOptions,
    });
};

/**
 * Fetch less content from the API.
 * @returns {void}
 * @private
 */
export const fetchLessContent = (currentPage, searchReportsResults) => {
    const newValue = currentPage - 1;

    dispatchSearchQueryEvent({
        ...(!searchReportsResults && {
            [SEARCH_IMG_QUERY_PARAMS.PAGE_AI]: newValue,
        }),
        ...(searchReportsResults && {
            [SEARCH_IMG_QUERY_PARAMS.PAGE_REPORT_AI]: newValue,
        }),
    });
};
